import type {
  FactSkillGroupKey,
  FactSkillItemView,
  FactSkillMemberView,
  FactSkillView,
  PowerGroupKey,
  PowerMemberView,
  PowersView,
  TeamMatchView,
} from "./match-result-codec";
import { levelFromPercent } from "@/lib/skill-levels";

export interface ResultPerson {
  id: string;
  name: string;
  profession?: string | null;
  avatarContent?: string | null;
  avatarMime?: string | null;
}

export interface DemographicsView {
  dept: string | null;
  gender: string | null;
  age: string | null;
}

export function buildDemographics(tm: TeamMatchView | null): DemographicsView {
  if (!tm) return { dept: null, gender: null, age: null };
  // Genesis buckets Persons with no gender set as "diverse". If the team has
  // zero declared m/f and only "d", treat that as "no data" and hide the chip
  // instead of misleadingly showing all-diverse.
  const declaredGender = tm.maleCount > 0 || tm.femaleCount > 0;
  const parts: string[] = [];
  if (tm.maleCount > 0) parts.push(`${tm.maleCount}m`);
  if (tm.femaleCount > 0) parts.push(`${tm.femaleCount}f`);
  if (tm.diverseCount > 0 && declaredGender) parts.push(`${tm.diverseCount}d`);
  const gender = parts.length ? parts.join("/") : null;

  let age: string | null = null;
  // Hide age entirely when no Person has a birth date filled – otherwise the
  // backend returns 0/0 and we end up displaying "0 yrs" which looks broken.
  const ageMissing = tm.minAge === 0 && tm.maxAge === 0;
  if (tm.minAge !== null && tm.maxAge !== null && !ageMissing) {
    if (tm.minAge === tm.maxAge) {
      age = `${tm.minAge} yrs`;
    } else if (tm.averageAge !== null) {
      age = `${tm.minAge}–${tm.maxAge} yrs (avg ${tm.averageAge.toFixed(1)})`;
    } else {
      age = `${tm.minAge}–${tm.maxAge} yrs`;
    }
  }

  const dept = tm.mostFrequentDepartmentCount > 0 ? `${tm.mostFrequentDepartmentCount}` : null;
  return { dept, gender, age };
}

export interface PowerClusterEntry {
  key: PowerGroupKey;
  score: number;
}

const CLUSTER_ORDER: PowerGroupKey[] = [
  "TEAM_SPIRIT",
  "COMMUNICATION",
  "RESILIENCE",
  "RESULT_ORIENTATION",
  "FUTURE_ORIENTATION",
  "INVENTIVENESS",
];

export function buildPowerClusters(powers: PowersView | null): PowerClusterEntry[] {
  if (!powers) return CLUSTER_ORDER.map((key) => ({ key, score: 0 }));
  return CLUSTER_ORDER.map((key) => ({ key, score: powers.groupPercent[key] ?? 0 }));
}

export interface TopPowerEntry {
  element: string;
  total: number;
}

export function buildTopPowers(powers: PowersView | null, limit: number): TopPowerEntry[] {
  if (!powers) return [];
  const sums = new Map<string, number>();
  for (const member of powers.teamMembers) {
    for (const item of member.items) {
      if (item.value === null) continue;
      sums.set(item.criterion.element, (sums.get(item.criterion.element) ?? 0) + item.value);
    }
  }
  return Array.from(sums.entries())
    .map(([element, total]) => ({ element, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

export interface HeatmapCell {
  element: string;
  group: string;
  value: number | null;
}

export interface HeatmapRow {
  personId: string;
  personName: string;
  totalPercent: number;
  cells: HeatmapCell[];
}

export function buildHeatmapRows(
  powers: PowersView | null,
  persons: ResultPerson[],
): HeatmapRow[] {
  if (!powers) return [];
  const elements = powers.criteria.map((c) => ({ element: c.element, group: c.group }));
  const personById = new Map(persons.map((p) => [p.id, p]));
  return powers.teamMembers
    .map((member) => {
      const personName = personById.get(member.personId)?.name ?? member.personId;
      const cells: HeatmapCell[] = elements.map((e) => {
        const found = member.items.find((i) => i.criterion.element === e.element);
        return { element: e.element, group: e.group, value: found?.value ?? null };
      });
      return {
        personId: member.personId,
        personName,
        // Von genesis geliefertes member.totalPercent ist mit #160 entfernt; aus den
        // per-Element-Atomen abgeleitet (ADR-0030 §2).
        totalPercent: memberPowerScore(member),
        cells,
      };
    })
    .sort((a, b) => b.totalPercent - a.totalPercent);
}

export function scoreBucket(value: number | null): 0 | 1 | 2 | 3 | 4 | 5 {
  if (value === null) return 0;
  if (value < 20) return 1;
  if (value < 40) return 2;
  if (value < 60) return 3;
  if (value < 80) return 4;
  return 5;
}

export interface RequirementColumn {
  label: string;
  rawName: string;
}

export interface RequirementRow {
  personId: string;
  personName: string;
  score: number;
  cells: Array<{ label: string; matched: boolean }>;
}

function conditionName(condition: unknown): string {
  if (condition && typeof condition === "object" && "name" in condition) {
    const v = (condition as { name?: unknown }).name;
    if (typeof v === "string") return v;
  }
  return "";
}

export function buildElementRequirementRows(
  factSkill: FactSkillView | null,
  persons: ResultPerson[],
): { columns: RequirementColumn[]; rows: RequirementRow[] } {
  if (!factSkill) return { columns: [], rows: [] };
  const powerCriteria = factSkill.criteria.filter((c) => c.group === "POWERS");
  if (powerCriteria.length === 0) return { columns: [], rows: [] };
  const columns: RequirementColumn[] = powerCriteria.map((c) => {
    const raw = conditionName(c.condition);
    return { rawName: raw, label: shortenRequirementLabel(raw) };
  });
  const personById = new Map(persons.map((p) => [p.id, p]));
  const rows: RequirementRow[] = factSkill.teamMembers
    .map((member) => {
      const cells = columns.map((col) => {
        const item = member.items.find((i) => conditionName(extractCondition(i.criterion)) === col.rawName);
        return { label: col.label, matched: Boolean(item?.matched) };
      });
      const score = cells.filter((c) => c.matched).length;
      return {
        personId: member.personId,
        personName: personById.get(member.personId)?.name ?? member.personId,
        score,
        cells,
      };
    })
    // score = Anzahl erfuellter Element-Kriterien; ersetzt Sortierung nach dem entfernten
    // member.totalPercent (ADR-0030 §2).
    .sort((a, b) => b.score - a.score);
  return { columns, rows };
}

// Per-requirement and per-group fulfillment derived client-side. Genesis's
// `factSkill` block carries per-person `items[].matched` (does this person
// satisfy the predicate?) and per-criterion `condition.composition` (the
// constraint: MIN/MAX/EXACT/ALL/ANY + count). We roll matchedCount up to a
// team-level fulfilled flag per requirement here — `teamMatch.conditionResults`
// from genesis only covers a subset of conditions, so we can't rely on it.
// The `factSkill.totalPercent` and `groupResultsPercent` genesis emits are
// person-weighted (matches ÷ persons × criteria) and don't answer "how many
// requirements does the team fulfill" — which is what this section shows.
export interface FactSkillFulfillment {
  totalFulfilled: number;
  totalCount: number;
  totalPercent: number;
  groups: Partial<Record<FactSkillGroupKey, { fulfilled: number; total: number; percent: number }>>;
  // Indexed by the criterion's position within factSkill.criteria. Name-based
  // indexing would collapse columns when genesis omits condition.name (which
  // happens for typed MatchCondition payloads — only a synthesised label).
  byCriterionIndex: boolean[];
  // Muss/Kann je Kriterium (aus dem isMust-Atom bzw. condition.requirement) und die Zahl der
  // vom Team verletzten Muss-Kriterien — damit Assess/Optimize Muss-Verstoesse unabhaengig vom
  // Score sichtbar machen (spark #151, ADR-0030 §1).
  mustByCriterionIndex: boolean[];
  violatedMustCount: number;
}

type CompositionOp = "MIN" | "MAX" | "EXACT" | "ALL" | "ANY";

function readComposition(condition: unknown): { operator: CompositionOp; value: number | null } | null {
  if (!condition || typeof condition !== "object") return null;
  const comp = (condition as { composition?: unknown }).composition;
  if (!comp || typeof comp !== "object") return null;
  const op = (comp as { operator?: unknown }).operator;
  const val = (comp as { value?: unknown }).value;
  if (op !== "MIN" && op !== "MAX" && op !== "EXACT" && op !== "ALL" && op !== "ANY") return null;
  return { operator: op, value: typeof val === "number" ? val : null };
}

function isCriterionFulfilled(condition: unknown, matchedCount: number, teamSize: number): boolean {
  const comp = readComposition(condition);
  // Fallback when composition is missing or unrecognised: treat "anyone matches" as fulfilled.
  if (!comp) return matchedCount >= 1;
  switch (comp.operator) {
    case "MIN":
      return comp.value !== null && matchedCount >= comp.value;
    case "MAX":
      return comp.value !== null && matchedCount <= comp.value;
    case "EXACT":
      return comp.value !== null && matchedCount === comp.value;
    case "ALL":
      return teamSize > 0 && matchedCount === teamSize;
    case "ANY":
      return matchedCount >= 1;
  }
}

export function buildFactSkillFulfillment(
  factSkill: FactSkillView | null,
  _teamMatch: TeamMatchView | null,
): FactSkillFulfillment {
  const empty: FactSkillFulfillment = {
    totalFulfilled: 0,
    totalCount: 0,
    totalPercent: 0,
    groups: {},
    byCriterionIndex: [],
    mustByCriterionIndex: [],
    violatedMustCount: 0,
  };
  if (!factSkill) return empty;

  const teamSize = factSkill.teamMembers.length;
  const byCriterionIndex: boolean[] = [];
  const mustByCriterionIndex: boolean[] = [];
  let violatedMustCount = 0;
  const groupAcc = new Map<string, { fulfilled: number; total: number }>();
  let totalFulfilled = 0;

  for (let idx = 0; idx < factSkill.criteria.length; idx++) {
    const c = factSkill.criteria[idx]!;
    let matched = 0;
    for (const m of factSkill.teamMembers) {
      if (m.items[idx]?.matched) matched += 1;
    }
    const fulfilled = isCriterionFulfilled(c.condition, matched, teamSize);
    byCriterionIndex.push(fulfilled);
    // isMust-Atom des ersten Members bevorzugt (ADR-0030), sonst condition.requirement.
    const firstItem = factSkill.teamMembers[0]?.items[idx];
    const must = firstItem
      ? itemRequirement(firstItem) === "MUST"
      : (c.condition as { requirement?: unknown } | null)?.requirement === "MUST";
    mustByCriterionIndex.push(must);
    if (must && !fulfilled) violatedMustCount += 1;
    const acc = groupAcc.get(c.group) ?? { fulfilled: 0, total: 0 };
    acc.total += 1;
    if (fulfilled) {
      acc.fulfilled += 1;
      totalFulfilled += 1;
    }
    groupAcc.set(c.group, acc);
  }

  const groups: FactSkillFulfillment["groups"] = {};
  for (const [g, v] of groupAcc.entries()) {
    groups[g as FactSkillGroupKey] = {
      fulfilled: v.fulfilled,
      total: v.total,
      percent: v.total === 0 ? 0 : v.fulfilled / v.total,
    };
  }

  const totalCount = factSkill.criteria.length;
  return {
    totalFulfilled,
    totalCount,
    totalPercent: totalCount === 0 ? 0 : totalFulfilled / totalCount,
    groups,
    byCriterionIndex,
    mustByCriterionIndex,
    violatedMustCount,
  };
}

// Best-effort human label for a FactSkill criterion. Genesis sometimes emits
// a synthesised `name` field, sometimes only the typed MatchCondition shape.
// `labels` is an id → name map for the ID-based conditions (department,
// location, country, gender, marital) — pass an empty map if unavailable.
export interface ConditionLabelLookup {
  jobDepartments: Map<string, string>;
  jobLocations: Map<string, string>;
  countries: Map<string, string>;
  genders: Map<string, string>;
  maritalStatuses: Map<string, string>;
  industries: Map<string, string>;
}

export interface ConditionLabelFormatters {
  labels: {
    department: string;
    location: string;
    country: string;
    citizenship: string;
    gender: string;
    maritalStatus: string;
    age: string;
    annualSalary: string;
    certificate: string;
    children: string;
    city: string;
    driversLicense: string;
    education: string;
    experience: string;
    industry: string;
    nationality: string;
    expertise: string;
    language: string;
  };
  exact(label: string, value: string, operator: unknown): string;
  present(label: string): string;
  threshold(label: string, value: string, operator: unknown): string;
  years(years: number): string;
  languageName(tag: string): string;
}

export const EMPTY_LABEL_LOOKUP: ConditionLabelLookup = {
  jobDepartments: new Map(),
  jobLocations: new Map(),
  countries: new Map(),
  genders: new Map(),
  maritalStatuses: new Map(),
  industries: new Map(),
};

/** Sentinel returned by {@link labelForCondition} when a condition can't be labelled. */
export const CONDITION_LABEL_UNKNOWN = "–";

export function labelForCondition(
  condition: unknown,
  lookup: ConditionLabelLookup,
  formatters: ConditionLabelFormatters,
): string {
  if (!condition || typeof condition !== "object") return CONDITION_LABEL_UNKNOWN;
  const obj = condition as Record<string, unknown>;
  const typename = typeof obj.__typename === "string" ? obj.__typename : "";
  const { labels } = formatters;

  switch (typename) {
    case "JobDepartmentCondition":
      return formatters.exact(labels.department, resolveId(obj.id, lookup.jobDepartments), obj.operator);
    case "JobLocationCondition":
      return formatters.exact(labels.location, resolveId(obj.id, lookup.jobLocations), obj.operator);
    case "CountryCondition":
      return formatters.exact(labels.country, resolveId(obj.id, lookup.countries), obj.operator);
    case "CitizenshipCondition":
      return formatters.exact(labels.citizenship, resolveId(obj.id, lookup.countries), obj.operator);
    case "GenderCondition":
      return formatters.exact(labels.gender, resolveId(obj.id, lookup.genders), obj.operator);
    case "MaritalCondition":
      return formatters.exact(labels.maritalStatus, resolveId(obj.id, lookup.maritalStatuses), obj.operator);
    case "AgeCondition":
      return formatters.threshold(labels.age, stringOf(obj.value), obj.operator);
    case "AnnualSalaryCondition":
      return formatters.threshold(labels.annualSalary, stringOf(obj.value), obj.operator);
    case "CertificateCondition":
      return formatters.exact(labels.certificate, stringOf(obj.value), obj.operator);
    case "ChildrenCountCondition":
      return formatters.threshold(labels.children, stringOf(obj.value), obj.operator);
    case "CityCondition": {
      const city = stringOf(obj.value);
      const radius = typeof obj.radiusKm === "number" ? obj.radiusKm : null;
      return formatters.exact(labels.city, radius == null ? city : `${city} · ${radius} km`, obj.operator);
    }
    case "DriversLicenseCondition":
      return formatters.exact(labels.driversLicense, stringOf(obj.value), obj.operator);
    case "EducationCondition":
      return formatters.exact(labels.education, stringOf(obj.value), obj.operator);
    case "ExperienceCondition":
      return formatters.threshold(labels.experience, formatYearsValue(obj.value, formatters), obj.operator);
    case "IndustryExperienceCondition": {
      const years = typeof obj.minimumYears === "number" ? obj.minimumYears : null;
      const industryId = typeof obj.industryId === "string" ? obj.industryId : "";
      const industry = industryId ? resolveId(industryId, lookup.industries) : stringOf(obj.name) || labels.industry;
      return years != null ? formatters.threshold(industry, formatters.years(years), obj.operator) : industry;
    }
    case "NationalityCondition":
      return formatters.exact(labels.nationality, stringOf(obj.value), obj.operator);
    case "ExpertiseCondition": {
      const skillName = typeof obj.expertise === "string" && obj.expertise.trim()
        ? obj.expertise
        : stringOf(obj.name) || labels.expertise;
      if (typeof obj.level === "number" && obj.level <= 0) return formatters.present(skillName);
      const skillLevel = typeof obj.level === "number" ? levelFromPercent(obj.level) : null;
      const level = skillLevel
        ? String(skillLevel.value)
        : typeof obj.level === "number"
          ? String(obj.level)
          : "";
      return level ? formatters.threshold(skillName, level, obj.operator) : skillName;
    }
    case "LanguageCondition": {
      const tag = typeof obj.languageTag === "string" ? obj.languageTag : "";
      const name = tag ? formatters.languageName(tag) : labels.language;
      const level = typeof obj.level === "string" ? obj.level : "";
      return level ? formatters.threshold(name, level, obj.operator) : name;
    }
    default:
      if (typeof obj.name === "string" && obj.name.length > 0) return obj.name;
      return typename.replace(/Condition$/, "") || CONDITION_LABEL_UNKNOWN;
  }
}

// Soll-Wert eines Kriteriums auf derselben Skala wie die actual-Verteilung
// (Expertise: Storage-Prozent 0..100, nicht Stufe 1..7), fuer den Soll-Marker
// auf der Verteilungs-Achse. null = Kriterium hat keinen numerischen Soll-Wert.
export function conditionNumericThreshold(condition: unknown): number | null {
  if (!condition || typeof condition !== "object") return null;
  const obj = condition as Record<string, unknown> & { __typename?: string };
  switch (obj.__typename) {
    case "ExpertiseCondition":
      return typeof obj.level === "number" && obj.level > 0 ? obj.level : null;
    case "IndustryExperienceCondition":
      return typeof obj.minimumYears === "number" ? obj.minimumYears : null;
    default:
      return typeof obj.value === "number" ? obj.value : null;
  }
}

function resolveId(id: unknown, map: Map<string, string>): string {
  if (typeof id !== "string") return "?";
  return map.get(id) ?? id;
}

function stringOf(v: unknown): string {
  return v === undefined || v === null ? "" : String(v);
}

function formatYearsValue(value: unknown, formatters: ConditionLabelFormatters): string {
  if (typeof value === "number") return formatters.years(value);
  if (typeof value === "string" && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return formatters.years(n);
  }
  return stringOf(value);
}

// "min 1", "max 5", "exact 2", "all", "any" — mirrors ai-research's
// formatConstraint for a familiar inline summary next to each requirement.
export function constraintSummary(condition: unknown): string {
  if (!condition || typeof condition !== "object") return "";
  const comp = (condition as { composition?: unknown }).composition;
  if (!comp || typeof comp !== "object") return "";
  const op = (comp as { operator?: unknown }).operator;
  const val = (comp as { value?: unknown }).value;
  switch (op) {
    case "MIN":
      return typeof val === "number" ? `min ${val}` : "min";
    case "MAX":
      return typeof val === "number" ? `max ${val}` : "max";
    case "EXACT":
      return typeof val === "number" ? `exact ${val}` : "exact";
    case "ALL":
      return "all";
    case "ANY":
      return "any";
    default:
      return "";
  }
}

// --- Bewerbung-Ranking (Muss-Gate + Gesamt-Erfüllung, Cluster A/B/C/unqualifiziert) ---
// "compute once, threshold many": genesis liefert je Kriterium requirement/fulfillment,
// die Clusterung passiert hier im Frontend und der Slider verschiebt nur die Schwellen.
export type RankCluster = "A" | "B" | "C" | "UNQUALIFIED";

export interface RankThresholds {
  a: number;
  b: number;
  // Untergrenze fuer C: darunter faellt ein Bewerber (trotz erfuellter Muss) aus A/B/C
  // in die unterste Gruppe. Optional, Default 0 = kein Score-Boden.
  c?: number;
}

export const DEFAULT_RANK_THRESHOLDS: RankThresholds = { a: 0.8, b: 0.5, c: 0 };

export interface RankedCriterion {
  condition: unknown;
  requirement: string;
  fulfillment: number;
  actual: string | null;
  aiReason: string | null;
}

export type RequirementLeniencyView = "LENIENT" | "MUST_STRICT" | "STRICT";

// Kulanz-Treffer: das Kriterium gilt als erfuellt, obwohl die geforderte Mindeststufe nicht
// belegt ist. Ohne genesis-Feld ableitbar: bei kulanter Anforderungspruefung stellt der
// AI-Fallback die Stufen-Frage gar nicht, und aiReason ist nur gesetzt, wenn der (immer
// stufen-strikte) Hard-Check vorher scheiterte. Jedes per AI bestandene Fachwissen-Kriterium
// mit gefordertem Level ist unter Kulanz also "vorhanden, Stufe nicht verifiziert".
// leniency kommt aus dem Design-Snapshot des Results (null = LENIENT, genesis-Default).
export function isLenientLevelMatch(
  condition: unknown,
  requirement: string,
  aiReason: string | null | undefined,
  met: boolean,
  leniency: RequirementLeniencyView | null | undefined,
): boolean {
  if (!met || !aiReason?.trim()) return false;
  const effective = leniency ?? "LENIENT";
  if (effective === "STRICT") return false;
  if (effective === "MUST_STRICT" && requirement === "MUST") return false;
  const c = condition as { __typename?: string; level?: number } | null;
  return c?.__typename === "ExpertiseCondition" && typeof c?.level === "number" && c.level > 0;
}

export interface RankedCandidate {
  personId: string;
  personName: string;
  cluster: RankCluster;
  score: number;
  qualified: boolean;
  metCount: number;
  totalCount: number;
  // Muss-Erfuellung getrennt ausweisbar (spark #151): der KANN-only-Score sagt nichts ueber die
  // bestandenen Muss-Kriterien, ein qualifizierter Kandidat mit schwachem KANN darf nicht als
  // "0 %"-Versager gelesen werden. mustMet/mustTotal treibt den Muss-Chip in der Zeile.
  mustMet: number;
  mustTotal: number;
  // KANN-Erfuellung fuer den Kann-Chip: Summe der Erfuellungsgrade, NICHT die Anzahl voll
  // erfuellter Kriterien. genesis liefert graduelle Werte (ratioFulfillment fuer minimumYears),
  // deshalb waere ein Zaehler irrefuehrend: 3x 0.8 erfuellt kein einziges Kriterium voll, ergibt
  // aber Score 80 %. kannSum/kannTotal === score; auf der Anforderungs-Achse kann das Badge
  // damit nicht vom Band abweichen (auf den anderen Achsen clustert deren Score, s. axisScore).
  kannSum: number;
  kannTotal: number;
  criteria: RankedCriterion[];
  // Zweite, ungemischte Achse (ADR-0030 / spark #151): gewichtetes Mittel der eigenen
  // Element-Scores des Kandidaten. null, wenn keine Powers vorliegen.
  powerFit: number | null;
  // Dritte, unabhaengige Achse: ganzheitliche AI-Eignung (0-1) aus aiAssessment.suitability
  // (genesis-Bewertung 0-100). null, wenn die AI keine Eignung geliefert hat (z.B. Alt-Blobs).
  suitabilityScore: number | null;
}

// Sortier-Achse der Rank-Liste. "requirements" = Anforderungs-Fit (Default, wie bisher),
// "power" = Power-Fit. Muss-Gate bleibt in beiden Faellen hartes Kriterium (qualifiziert zuerst).
export type RankSortBy = "smycles" | "requirements" | "power";

// Power-Fit je Kandidat: gewichtetes Mittel der eigenen Element-Scores (0-100), Gewicht aus den
// Team-Power-Wuenschen (0-10, elementWeights je CreativityElement). Fehlt ein Gewicht -> 1
// (gleichgewichtet). Kandidaten ohne Element-Werte -> 0. Unabhaengig vom Anforderungs-Fit.
export function buildPowerFit(
  powers: PowersView | null,
  elementWeights?: Readonly<Record<string, number>>,
): Map<string, number> {
  const byPerson = new Map<string, number>();
  if (!powers) return byPerson;
  for (const member of powers.teamMembers) {
    let weightSum = 0;
    let weightedValueSum = 0;
    for (const item of member.items) {
      if (item.value == null) continue;
      const weight = elementWeights?.[item.criterion.element] ?? 1;
      if (weight <= 0) continue;
      weightSum += weight;
      weightedValueSum += weight * item.value;
    }
    byPerson.set(member.personId, weightSum > 0 ? weightedValueSum / weightSum : 0);
  }
  return byPerson;
}

function itemRequirement(item: FactSkillItemView): string {
  // Breakdown-Atom zuerst (ADR-0030 §2): genesis liefert isMust direkt. Fallback auf
  // condition.requirement fuer Alt-Blobs vor #160.
  if (item.isMust != null) return item.isMust ? "MUST" : "OPTIONAL";
  const cond = extractCondition(item.criterion) as { requirement?: unknown } | null;
  return cond?.requirement === "MUST" ? "MUST" : "OPTIONAL";
}

function itemFulfillment(item: FactSkillItemView): number {
  if (typeof item.fulfillment === "number") return item.fulfillment;
  return item.matched ? 1 : 0;
}

// Kandidaten-Score aus den Breakdown-Atomen: ungewichtetes Mittel der Erfuellung.
// Ersetzt das frueher von genesis gelieferte member.totalPercent (mit #160 entfernt, ADR-0030 §2).
export function memberFactSkillScore(member: FactSkillMemberView): number {
  if (member.items.length === 0) return 0;
  return member.items.reduce((sum, i) => sum + itemFulfillment(i), 0) / member.items.length;
}

function memberPowerScore(member: PowerMemberView): number {
  const values = member.items.map((i) => i.value).filter((v): v is number => v != null);
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function buildCandidateRanking(
  factSkill: FactSkillView | null,
  persons: ResultPerson[],
  thresholds: RankThresholds = DEFAULT_RANK_THRESHOLDS,
  powerFitById?: Map<string, number>,
  sortBy: RankSortBy = "smycles",
): RankedCandidate[] {
  if (!factSkill) return [];
  const personById = new Map(persons.map((p) => [p.id, p]));
  return factSkill.teamMembers
    .map((member): RankedCandidate => {
      let mustFailed = false;
      let kannSum = 0;
      let kannCount = 0;
      let metCount = 0;
      let mustMet = 0;
      let mustTotal = 0;
      const criteria: RankedCriterion[] = [];
      for (const item of member.items) {
        const requirement = itemRequirement(item);
        const fulfillment = itemFulfillment(item);
        criteria.push({
          condition: extractCondition(item.criterion),
          requirement,
          fulfillment,
          actual: item.actual ?? null,
          aiReason: item.aiReason ?? null,
        });
        if (fulfillment >= 1) metCount += 1;
        if (requirement === "MUST") {
          mustTotal += 1;
          if (fulfillment < 1) mustFailed = true;
          else mustMet += 1;
        } else {
          kannSum += fulfillment;
          kannCount += 1;
        }
      }
      criteria.sort((a, b) => Number(b.requirement === "MUST") - Number(a.requirement === "MUST"));
      const qualified = !mustFailed;
      // Score = ungewichtetes Mittel der KANN-Erfuellung (ADR-0030 §3 / map #137). MUSS ist reines
      // Gate: bei allen Qualifizierten ohnehin 1.0, es wuerde den Wert nur konstant nach oben
      // verzerren (Kompression + nicht match-uebergreifend vergleichbar). Ohne KANN-Kriterium gibt
      // es nichts zu differenzieren -> 1.0.
      const score = kannCount === 0 ? 1 : kannSum / kannCount;
      const rawSuitability = member.aiAssessment?.suitability;
      const suitabilityScore =
        typeof rawSuitability === "number" && Number.isFinite(rawSuitability)
          ? Math.max(0, Math.min(100, rawSuitability)) / 100
          : null;
      const powerFit = powerFitById?.get(member.personId) ?? null;
      // Die A/B/C-Schwellen clustern ueber die gewaehlte Sortier-Achse, damit Band und
      // Reihenfolge dieselbe Groesse meinen (92 % Smycles-Score bei Schwelle A=80 landet in A).
      // Fehlt der Achsen-Wert (AI aus, keine Powers), faellt die Clusterung wie die Sortierung
      // auf den Anforderungs-Fit zurueck. powerFit ist 0..100, Schwellen sind 0..1.
      const axisScore =
        sortBy === "smycles"
          ? (suitabilityScore ?? score)
          : sortBy === "power"
            ? (powerFit != null ? powerFit / 100 : score)
            : score;
      // Unterste Gruppe speist sich aus zwei Wegen: Muss-Gate gescheitert ODER Achsen-Score unter
      // der C-Schwelle. `qualified` bleibt Muss-only, damit die Anzeige (% vs. erfuellt/gesamt)
      // unterscheidbar bleibt.
      let cluster: RankCluster;
      if (!qualified || axisScore < (thresholds.c ?? 0)) cluster = "UNQUALIFIED";
      else if (axisScore >= thresholds.a) cluster = "A";
      else if (axisScore >= thresholds.b) cluster = "B";
      else cluster = "C";
      return {
        personId: member.personId,
        personName: personById.get(member.personId)?.name ?? member.personId,
        cluster,
        score,
        qualified,
        metCount,
        totalCount: member.items.length,
        mustMet,
        mustTotal,
        kannSum,
        kannTotal: kannCount,
        criteria,
        powerFit,
        suitabilityScore,
      };
    })
    .sort((x, y) => {
      // Muss-Gate bleibt in allen Achsen das erste Ordnungskriterium: qualifizierte zuerst.
      if (x.qualified !== y.qualified) return x.qualified ? -1 : 1;
      if (sortBy === "smycles") {
        const d = (y.suitabilityScore ?? -1) - (x.suitabilityScore ?? -1);
        if (d !== 0) return d;
        // gleichauf oder keine Eignung -> nach Anforderungs-Score sortieren
      }
      if (sortBy === "power") return (y.powerFit ?? -1) - (x.powerFit ?? -1);
      return y.score - x.score;
    });
}

function extractCondition(criterion: unknown): unknown {
  if (criterion && typeof criterion === "object" && "condition" in criterion) {
    return (criterion as { condition?: unknown }).condition;
  }
  return criterion;
}

function conditionKey(item: FactSkillItemView): string {
  // Stabiler genesis-Schluessel bevorzugt (ADR-0030); Fallback auf serialisierte Condition
  // fuer Alt-Blobs, die conditionKey noch nicht mitliefern.
  const crit = item.criterion as { conditionKey?: unknown } | null;
  if (crit && typeof crit === "object" && typeof crit.conditionKey === "string" && crit.conditionKey.length > 0) {
    return crit.conditionKey;
  }
  const condition = extractCondition(item.criterion);
  return condition === undefined ? "undefined" : JSON.stringify(condition);
}

function shortenRequirementLabel(raw: string): string {
  const match = raw.match(/POWER:([A-Z]+)/);
  if (match && match[1]) return match[1];
  return raw.slice(0, 6).toUpperCase();
}

// Meta-Analyse ueber das Pruefen-Ergebnis: welche Anforderungen erfuellen die meisten,
// und welches Muss-Kriterium schliesst am haeufigsten aus. Genesis liefert die
// Kriterienlisten pro Bewerber in gleicher Reihenfolge; die Aggregation verlaesst
// sich aber nicht darauf, sondern ordnet Items pro Bewerber ueber den Condition-Key
// zu. Fehlt ein Item bei einem Bewerber, zaehlt es als nicht erfuellt.
export interface RankingInsight {
  condition: unknown;
  requirement: string;
  count: number;
  total: number;
}

export interface RankingCriterionInsight {
  condition: unknown;
  requirement: string;
  metCount: number;
  failedCount: number;
  partialCount: number;
  // aiCount zaehlt alle Items mit AI-Begruendung; aiMetCount nur die davon,
  // die als erfuellt zaehlen (AI hat den Hard-Check von 0 auf 1 gekippt).
  aiCount: number;
  aiMetCount: number;
  missingActualCount: number;
  expectedActualCount: number;
  averageFulfillment: number;
  total: number;
  distribution: RankingActualDistribution | null;
}

export interface RankingPoolInsights {
  total: number;
  counts: Record<RankCluster, number>;
  averageScore: number | null;
  medianScore: number | null;
  topGap: number | null;
  qualifiedCount: number;
}

export type RankingActualDistribution =
  | {
      kind: "numeric";
      condition: unknown;
      present: number;
      missing: number;
      min: number;
      average: number;
      median: number;
      standardDeviation: number;
      max: number;
    }
  | {
      kind: "text";
      condition: unknown;
      present: number;
      missing: number;
      topValue: string;
      topCount: number;
      distinctCount: number;
    };

export interface RankingAiCriterionInsight {
  condition: unknown;
  reasonCount: number;
  candidateCount: number;
}

export interface RankingAiInsights {
  reasonCount: number;
  candidateCount: number;
  criteria: RankingAiCriterionInsight[];
  totalItems: number;
}

export interface RankingDataQualityInsights {
  missingActualCount: number;
  expectedActualCount: number;
  topMissing: RankingCriterionInsight | null;
}

export type RankingRecommendation =
  | { type: "must"; criterion: RankingCriterionInsight }
  | { type: "data"; criterion: RankingCriterionInsight }
  | { type: "ai"; reasonCount: number; candidateCount: number }
  | { type: "bottleneck"; criterion: RankingCriterionInsight };

export interface RankingInsights {
  hardestMust: RankingInsight | null;
  pool: RankingPoolInsights;
  // Beste Kriterien nach Erfuellungszaehlung (inkl. AI-Flips): traegt auch bei
  // FULL_AI Information, wo die Ist-Wert-Verteilungen mangels actual leer sind.
  topFulfilled: RankingCriterionInsight[];
  bottlenecks: RankingCriterionInsight[];
  ai: RankingAiInsights;
  dataQuality: RankingDataQualityInsights;
  recommendation: RankingRecommendation | null;
}

const EMPTY_POOL: RankingPoolInsights = {
  total: 0,
  counts: { A: 0, B: 0, C: 0, UNQUALIFIED: 0 },
  averageScore: null,
  medianScore: null,
  topGap: null,
  qualifiedCount: 0,
};

const EMPTY_INSIGHTS: RankingInsights = {
  hardestMust: null,
  pool: EMPTY_POOL,
  topFulfilled: [],
  bottlenecks: [],
  ai: { reasonCount: 0, candidateCount: 0, criteria: [], totalItems: 0 },
  dataQuality: { missingActualCount: 0, expectedActualCount: 0, topMissing: null },
  recommendation: null,
};

export function buildRankingInsights(
  factSkill: FactSkillView | null,
  ranked: RankedCandidate[] = [],
): RankingInsights {
  if (!factSkill || factSkill.teamMembers.length === 0) {
    return EMPTY_INSIGHTS;
  }
  const members = factSkill.teamMembers;
  const total = members.length;
  const referenceItems = members[0]?.items ?? [];
  // Items pro Bewerber nach Condition-Key gruppiert (FIFO bei doppelten
  // Kriterien), damit die Zuordnung nicht von der Listen-Reihenfolge abhaengt.
  const itemsByCondition = members.map((member) => {
    const byKey = new Map<string, FactSkillItemView[]>();
    for (const item of member.items) {
      const key = conditionKey(item);
      const queue = byKey.get(key);
      if (queue) queue.push(item);
      else byKey.set(key, [item]);
    }
    return byKey;
  });
  let hardestMust: RankingInsight | null = null;
  const criterionInsights: RankingCriterionInsight[] = [];
  const aiCandidates = new Set<string>();
  const aiCriteria: RankingAiCriterionInsight[] = [];
  let reasonCount = 0;
  let missingActualCount = 0;
  let expectedActualCount = 0;
  let totalItems = 0;
  for (const first of referenceItems) {
    const requirement = itemRequirement(first);
    const condition = extractCondition(first.criterion);
    const key = conditionKey(first);
    const expectsActual = expectsActualValue(condition);
    let met = 0;
    let partial = 0;
    let failed = 0;
    let fulfillmentSum = 0;
    let aiCount = 0;
    let aiMetCount = 0;
    let missingForCriterion = 0;
    let expectedForCriterion = 0;
    const aiCandidateIds = new Set<string>();
    const actuals: string[] = [];
    for (const [memberIndex, member] of members.entries()) {
      const item = itemsByCondition[memberIndex]?.get(key)?.shift();
      totalItems += item ? 1 : 0;
      const hasAiReason = Boolean(item?.aiReason?.trim());
      const fulfillment = item ? itemFulfillment(item) : 0;
      fulfillmentSum += fulfillment;
      if (fulfillment >= 1) met += 1;
      else {
        failed += 1;
        if (fulfillment > 0) partial += 1;
      }
      if (hasAiReason) {
        reasonCount += 1;
        aiCount += 1;
        if (fulfillment >= 1) aiMetCount += 1;
        aiCandidates.add(member.personId);
        aiCandidateIds.add(member.personId);
      }
      if (expectsActual && item && !hasAiReason) {
        expectedActualCount += 1;
        expectedForCriterion += 1;
      }
      if (item?.actual != null && String(item.actual).trim().length > 0) {
        actuals.push(String(item.actual));
      } else if (expectsActual && item && !hasAiReason) {
        missingActualCount += 1;
        missingForCriterion += 1;
      }
    }
    if (requirement === "MUST" && failed > 0 && (hardestMust === null || failed > hardestMust.count)) {
      hardestMust = { condition, requirement, count: failed, total };
    }
    const criterionInsight: RankingCriterionInsight = {
      condition,
      requirement,
      metCount: met,
      failedCount: failed,
      partialCount: partial,
      aiCount,
      aiMetCount,
      missingActualCount: missingForCriterion,
      expectedActualCount: expectedForCriterion,
      averageFulfillment: total > 0 ? fulfillmentSum / total : 0,
      total,
      distribution: buildActualDistribution(condition, actuals, total),
    };
    criterionInsights.push(criterionInsight);
    if (aiCount > 0) {
      aiCriteria.push({ condition, reasonCount: aiCount, candidateCount: aiCandidateIds.size });
    }
  }
  const topFulfilled = criterionInsights
    .filter((c) => c.metCount > 0)
    .sort((a, b) => {
      if (a.metCount !== b.metCount) return b.metCount - a.metCount;
      return b.averageFulfillment - a.averageFulfillment;
    })
    .slice(0, 3);
  const bottlenecks = criterionInsights
    .filter((c) => c.metCount < c.total || c.partialCount > 0)
    .sort((a, b) => {
      if (a.averageFulfillment !== b.averageFulfillment) return a.averageFulfillment - b.averageFulfillment;
      return b.failedCount - a.failedCount;
    })
    .slice(0, 3);
  const topMissing = [...criterionInsights]
    .filter((c) => c.missingActualCount > 0)
    .sort((a, b) => b.missingActualCount - a.missingActualCount)[0] ?? null;
  const topAiCriteria = aiCriteria.sort((a, b) => b.reasonCount - a.reasonCount).slice(0, 3);
  const pool = buildPoolInsights(ranked);
  return {
    hardestMust,
    pool,
    topFulfilled,
    bottlenecks,
    ai: {
      reasonCount,
      candidateCount: aiCandidates.size,
      criteria: topAiCriteria,
      totalItems,
    },
    dataQuality: { missingActualCount, expectedActualCount, topMissing },
    recommendation: chooseRankingRecommendation({
      hardestMustInsight: criterionInsights
        .filter((c) => c.requirement === "MUST" && c.failedCount > 0)
        .sort((a, b) => b.failedCount - a.failedCount)[0] ?? null,
      topMissing,
      aiReasonCount: reasonCount,
      aiCandidateCount: aiCandidates.size,
      candidateTotal: total,
      bottleneck: bottlenecks[0] ?? null,
    }),
  };
}

function buildPoolInsights(ranked: RankedCandidate[]): RankingPoolInsights {
  if (ranked.length === 0) return EMPTY_POOL;
  const counts: Record<RankCluster, number> = { A: 0, B: 0, C: 0, UNQUALIFIED: 0 };
  for (const candidate of ranked) counts[candidate.cluster] += 1;
  const qualified = ranked.filter((c) => c.qualified);
  const scores = qualified.map((c) => c.score).sort((a, b) => b - a);
  return {
    total: ranked.length,
    counts,
    averageScore: scores.length ? scores.reduce((sum, value) => sum + value, 0) / scores.length : null,
    medianScore: median(scores),
    topGap: scores.length >= 3 ? scores[0]! - scores[2]! : null,
    qualifiedCount: qualified.length,
  };
}

function buildActualDistribution(
  condition: unknown,
  actuals: string[],
  total: number,
): RankingActualDistribution | null {
  if (actuals.length === 0 || suppressActualDistribution(condition)) return null;
  if (isNumericActualCondition(condition)) {
    let values = actuals.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    // Fachwissen-Stufe 0 heisst "unbewertet", nicht "Level 0" (genesis persistiert bewertete
    // Level als ~11..100). Unbewertete verfaelschen Median/Ø als Nullen-Schwemme -> raus.
    // Bei Jahren (Experience/Industry) ist 0 dagegen ein echter Wert und bleibt drin.
    if (conditionTypename(condition) === "ExpertiseCondition") {
      values = values.filter((v) => v > 0);
    }
    // Ein Einzelwert ist keine Verteilung: Median/Ø/SD ueber n=1 suggerieren eine
    // Pool-Aussage, die es nicht gibt. Dann lieber keine Achse (Balken bleibt).
    if (values.length < 2) return null;
    const average = values.reduce((sum, value) => sum + value, 0) / values.length;
    return {
      kind: "numeric",
      condition,
      present: values.length,
      missing: Math.max(0, total - values.length),
      min: values[0]!,
      average,
      median: median(values) ?? values[0]!,
      standardDeviation: standardDeviation(values, average),
      max: values[values.length - 1]!,
    };
  }
  const counts = new Map<string, number>();
  for (const actual of actuals) counts.set(actual, (counts.get(actual) ?? 0) + 1);
  const [topValue, topCount] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];
  if (!topValue) return null;
  return {
    kind: "text",
    condition,
    present: actuals.length,
    missing: Math.max(0, total - actuals.length),
    topValue,
    topCount,
    distinctCount: counts.size,
  };
}

function isNumericActualCondition(condition: unknown): boolean {
  const typename = conditionTypename(condition);
  return typename === "IndustryExperienceCondition" ||
    typename === "ExperienceCondition" ||
    typename === "ExpertiseCondition";
}

function suppressActualDistribution(condition: unknown): boolean {
  const typename = conditionTypename(condition);
  return typename === "GenderCondition" ||
    typename === "AgeCondition" ||
    typename === "ChildrenCountCondition" ||
    typename === "MaritalCondition" ||
    typename === "CitizenshipCondition" ||
    typename === "NationalityCondition";
}

function expectsActualValue(condition: unknown): boolean {
  const typename = conditionTypename(condition);
  return typename === "AgeCondition" ||
    typename === "AnnualSalaryCondition" ||
    typename === "CertificateCondition" ||
    typename === "ChildrenCountCondition" ||
    typename === "CitizenshipCondition" ||
    typename === "CityCondition" ||
    typename === "CountryCondition" ||
    typename === "DriversLicenseCondition" ||
    typename === "EducationCondition" ||
    typename === "ExperienceCondition" ||
    typename === "ExpertiseCondition" ||
    typename === "GenderCondition" ||
    typename === "IndustryExperienceCondition" ||
    typename === "JobDepartmentCondition" ||
    typename === "JobLocationCondition" ||
    typename === "LanguageCondition" ||
    typename === "MaritalCondition" ||
    typename === "NationalityCondition";
}

function conditionTypename(condition: unknown): string {
  return condition && typeof condition === "object" && typeof (condition as { __typename?: unknown }).__typename === "string"
    ? ((condition as { __typename: string }).__typename)
    : "";
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid]!;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function standardDeviation(values: number[], average: number): number {
  if (values.length <= 1) return 0;
  const variance = values.reduce((sum, value) => sum + (value - average) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function chooseRankingRecommendation({
  hardestMustInsight,
  topMissing,
  aiReasonCount,
  aiCandidateCount,
  candidateTotal,
  bottleneck,
}: {
  hardestMustInsight: RankingCriterionInsight | null;
  topMissing: RankingCriterionInsight | null;
  aiReasonCount: number;
  aiCandidateCount: number;
  candidateTotal: number;
  bottleneck: RankingCriterionInsight | null;
}): RankingRecommendation | null {
  if (hardestMustInsight && hardestMustInsight.failedCount > 0) {
    return { type: "must", criterion: hardestMustInsight };
  }
  if (topMissing && topMissing.missingActualCount / Math.max(1, topMissing.total) >= 0.4) {
    return { type: "data", criterion: topMissing };
  }
  if (aiReasonCount > 0 && aiCandidateCount / Math.max(1, candidateTotal) >= 0.4) {
    return { type: "ai", reasonCount: aiReasonCount, candidateCount: aiCandidateCount };
  }
  if (bottleneck && bottleneck.averageFulfillment < 0.75) {
    return { type: "bottleneck", criterion: bottleneck };
  }
  return null;
}

// Umkreis-Insight (Radius-Karte in der Match-Analyse). Die Distanz steht nur im deterministisch
// von genesis formatierten Begruendungssatz ("Distanz rund 21 km, ..."), ein eigenes Feld im
// Result-Blob gibt es (noch) nicht; das Satzformat besitzen wir auf beiden Seiten.
const RADIUS_REASON_PATTERN = /^(?:Distanz rund|Distance around) (\d+(?:[.,]\d+)?) km/;

export interface RadiusInsightPoint {
  personId: string;
  personName: string;
  distanceKm: number;
  inside: boolean;
}

export interface RadiusInsight {
  city: string;
  radiusKm: number;
  // Aufsteigend nach Distanz sortiert.
  points: RadiusInsightPoint[];
  // Kandidaten mit Radius-Kriterium, aber ohne parsebare Distanz (GeoNames-Fallback auf KI).
  missing: { personId: string; personName: string }[];
}

function radiusCondition(condition: unknown): { city: string; radiusKm: number } | null {
  const cond = condition as { __typename?: unknown; value?: unknown; radiusKm?: unknown } | null;
  if (
    cond
    && cond.__typename === "CityCondition"
    && typeof cond.value === "string"
    && typeof cond.radiusKm === "number"
    && cond.radiusKm > 0
  ) {
    return { city: cond.value, radiusKm: cond.radiusKm };
  }
  return null;
}

export function buildRadiusInsight(ranked: RankedCandidate[]): RadiusInsight | null {
  let target: { city: string; radiusKm: number } | null = null;
  for (const candidate of ranked) {
    for (const criterion of candidate.criteria) {
      target = radiusCondition(criterion.condition);
      if (target) break;
    }
    if (target) break;
  }
  if (!target) return null;
  const { city, radiusKm } = target;
  const points: RadiusInsightPoint[] = [];
  const missing: { personId: string; personName: string }[] = [];
  for (const candidate of ranked) {
    const criterion = candidate.criteria.find((item) => {
      const cond = radiusCondition(item.condition);
      return cond?.city === city && cond?.radiusKm === radiusKm;
    });
    if (!criterion) continue;
    const distanceRaw = criterion.aiReason?.match(RADIUS_REASON_PATTERN)?.[1];
    if (!distanceRaw) {
      missing.push({ personId: candidate.personId, personName: candidate.personName });
      continue;
    }
    const distanceKm = Number(distanceRaw.replace(",", "."));
    points.push({
      personId: candidate.personId,
      personName: candidate.personName,
      distanceKm,
      inside: distanceKm <= radiusKm,
    });
  }
  if (points.length === 0) return null;
  points.sort((a, b) => a.distanceKm - b.distanceKm);
  return { city, radiusKm, points, missing };
}
